import { CONSTANTS__client } from '@/constants/CONSTANTS__client';
import { use_slice_generation } from '@/hooks/store/use_slices';
import { HOOK_GRID2D_CELL_GENERATION__class_name_hover } from '@/pages/_base/grid2d_cell/_hooks/HOOK_GRID2D_CELL_GENERATION__class_name_hover';
import { assert_instanceof } from '@shared/asserts/utils_asserts';
import { DTO__grid2d } from '@shared/dto/grid2d/DTO__grid2d';
import { DTO__grid2d_generation } from '@shared/dto/grid2d/DTO__grid2d_generation';
import { DTO__grid2d_cell_generation } from '@shared/dto/grid2d_cell/DTO__grid2d_cell_generation';
import React, { useCallback, useMemo } from 'react';
import { GRID2D_CELL__generation_waiting } from './GRID2D_CELL__generation_waiting';
import { GRID2D_CELL__image_content_or_empty } from './GRID2D_CELL__image_content_or_empty';
import { GRID2D_CELL__loading_image } from './GRID2D_CELL__loading_image';

interface PROPS {
  x: number;
  y: number;
  //
  cell_height: number;
  cell_width: number;
  //
  on_mouse_leave_cell: (cell: DTO__grid2d_cell_generation) => void;
  on_mouse_enter_cell: (cell: DTO__grid2d_cell_generation) => void;
  //
  grid2d_generation_current: DTO__grid2d;
}

export const GRID2D_CELL__generation = React.memo(
  ({ x, y, cell_height, cell_width, on_mouse_enter_cell, on_mouse_leave_cell, grid2d_generation_current }: PROPS) => {
    /*
     * INPUTS
     */
    assert_instanceof(grid2d_generation_current, DTO__grid2d_generation);

    /*
     * STORE
     */

    const { generation_current } = use_slice_generation(['generation_current']);

    /*
     * STATE
     */
    const cell = useMemo(() => {
      const grid2d_chunk_generation = grid2d_generation_current.get_generation_chunk();

      return grid2d_chunk_generation.get_cell_by_coordinates(x, y);
    }, [grid2d_generation_current]);

    let class_name_hover = HOOK_GRID2D_CELL_GENERATION__class_name_hover(cell);

    /*
     * METHODS
     */

    const can_show_buttons = useCallback(() => {
      return cell.is_image_generated_on_generation(generation_current._id);
    }, [cell, generation_current._id]);

    /*
     * RENDERS
     */

    const render_cell = useMemo(() => {
      let out = <></>;

      const cell_new = cell.clone({ grid2d_id: grid2d_generation_current._id, x, y }); //need set grid_id for empty cells

      if (!cell_new.hash_sha256_content.is_empty_hash_sha256()) {
        out = (
          <GRID2D_CELL__image_content_or_empty
            key={`${generation_current._id}-${y}-${x}`}
            //data
            entity={generation_current}
            grid2d={grid2d_generation_current}
            cell={cell_new}
            //optional params
            can_show_buttons={can_show_buttons()}
            className={cell_new.is_image_generated_on_generation(generation_current._id) ? '' : 'opacity-50'}
            //size
            height={cell_height}
            width={cell_width}
            //
            use_offset_from_slug={false}
          />
        );
      } else {
        out = generation_current.is_in_progress ? (
          <GRID2D_CELL__loading_image key={`${generation_current._id}-${y}-${x}`} height={cell_height} width={cell_width} />
        ) : (
          <GRID2D_CELL__generation_waiting column_index={x} height={cell_height} width={cell_width} key={`${generation_current._id}-${y}-${x}`} />
        );
      }

      return out;
    }, [cell, cell_height, cell_width]);

    function render_view() {
      let out = (
        <div
          onMouseEnter={() => on_mouse_enter_cell(cell)}
          onMouseLeave={() => on_mouse_leave_cell(cell)}
          className={`absolute ${class_name_hover}`}
          style={{
            top: 0,
            left: 0,
            height: `${cell_height + CONSTANTS__client.GENERATION_GRID_GAP}px`,
            width: `${cell_width + CONSTANTS__client.GENERATION_GRID_GAP}px`,
            transform: `translate3d(${x * (cell_width + CONSTANTS__client.GENERATION_GRID_GAP)}px,
            ${y * (cell_height + CONSTANTS__client.GENERATION_GRID_GAP)}px, 0)`,
          }}
        >
          {render_cell}
        </div>
      );

      return out;
    }

    /*
     * HTML
     */
    if (grid2d_generation_current.is_stub()) return null;

    return render_view();
  },
);
